/**
 * Connection-operation overlay engine for optimistic connection mutations.
 *
 * The query cache holds pristine server truth (`base`); in-flight mutations are an
 * ordered operation log (TanStack Query's mutation cache); every consumer renders
 * `applyOperations(base, pendingOperations)`. Because application always starts from a
 * pristine base and never mutates in place, optimistic state composes and rolls back by
 * construction: an errored mutation drops its operation, a completed one folds server
 * truth into `base`. No inverse to compute, no position bookkeeping.
 *
 * Framework-agnostic: no Relay or event-bus dependency.
 */
import {applyOperation, type ApplyOptions} from './record-document-adapter'
import type {ConnectionOperation, EntityOperation, PendingConnectionOperation} from './operation-types'

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Preserve the package's public type imports.
export type * from './operation-types'

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Preserve public imports after extraction.
export {applyOperation, type ApplyOptions}

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Preserve public helper imports after extraction.
export {collectIdentities, OPTIMISTIC_KEY, removeIncorporatedOperation} from './record-document-adapter'

export function isEntityOperation(operation: ConnectionOperation): operation is EntityOperation {
  return (
    operation.type === 'setFields' ||
    operation.type === 'upsertEntityFieldItem' ||
    operation.type === 'removeEntityFieldItem' ||
    operation.type === 'deltaEntityField' ||
    operation.type === 'deltaEntityFieldValues' ||
    operation.type === 'updateEntityFieldItems'
  )
}

export function operationNeedsIncorporation(operation: ConnectionOperation): boolean {
  return (
    operation.type === 'deltaEntityField' ||
    operation.type === 'deltaEntityFieldValues' ||
    operation.type === 'updateEntityFieldItems' ||
    ('counts' in operation && Boolean(operation.counts?.length))
  )
}

/** Semantic mutation-cache path for operations that edit fields on a document node rather
 * than structurally changing one of its connections. */
export const DOCUMENT_FIELDS_PATH = ['documentFields']

/**
 * A collision-free key for a connection path. `join(delimiter)` is lossy (`[]` and `['']`
 * both collapse to `''`, and a delimiter inside a segment aliases a different structure);
 * JSON encodes the array structure and each segment exactly.
 */
export function connectionPathKey(path: readonly string[]): string {
  return JSON.stringify(path)
}

/**
 * Applies every pending operation in dispatch order onto the pristine `base` — what a
 * consumer renders; see `useOptimisticData`.
 */
export function applyOperations<T>(
  base: T,
  pendingOperations: PendingConnectionOperation[],
  options: ApplyOptions = {},
): T {
  return pendingOperations.reduce<T>(
    (doc, {ownerId, operation, operationId}) => applyOperation(doc, ownerId, operation, {...options, operationId}),
    base,
  )
}
